import { Log } from '@swyft/core';
import { User, UserManager, WebStorageStateStore } from 'oidc-client';

export interface AuthServiceConfig {
  authority: string;
  clientId: string;
  basePath: string;
  handleRedirectCallbackState: (state: string) => void;
}

export class AuthService {
  userManager: UserManager;

  permissions: string[];
  config: AuthServiceConfig;

  constructor(config: AuthServiceConfig) {
    this.permissions = [];
    this.config = config;

    this.userManager = new UserManager({
      authority: config.authority,
      client_id: config.clientId,

      scope: 'openid profile api',

      response_type: 'code',

      automaticSilentRenew: false,

      redirect_uri: `${window.location.origin}${config.basePath}/signin-redirect`,
      silent_redirect_uri: `${window.location.origin}${config.basePath}/silent-renew.html`,
      post_logout_redirect_uri: `${window.location.origin}${config.basePath}/signout-redirect`,

      loadUserInfo: false,
      userStore: new WebStorageStateStore({ store: window.sessionStorage })
    });

    this.userManager.events.addUserLoaded(this.userLoaded);

    // Oidc Logger
    // Log.logger = console;
    // Log.level = Log.DEBUG;
  }

  userLoaded = (user: User) => {
    Log.info(`User Loaded: Session Expiry ${user.expires_in}s`);
    localStorage.setItem(this.StorageCheckAuth, 'true');
    this.permissions = this.getPermissionsFromAccessToken(user.access_token);
  };

  hasPermission = (permission: string) => {
    // Log.debug(`Check Permission: ${permission}`);
    return this.permissions.includes(permission);
  };

  signInRedirectCallback = async () => {
    Log.debug('AuthService:signInRedirectCallback');
    return await this.userManager.signinRedirectCallback().then((user) => {
      if (user.state) {
        this.config.handleRedirectCallbackState(user.state);
      } else {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    });
  };

  StorageCheckAuth = 'check_auth';

  getUser = async (): Promise<User | undefined> => {
    // Log.debug("AuthService:getUser");

    var user = await this.userManager.getUser();

    if (user) {
      this.userLoaded(user);
    } else {
      this.permissions = [];
    }

    return user || undefined;
  };

  tryGetUser = async (): Promise<User | undefined> => {
    // Log.debug("AuthService:tryGetUser");

    if (!localStorage.getItem(this.StorageCheckAuth)) {
      return;
    }

    var user = await this.getUser();

    if (!user || user.expired) {
      try {
        user = await this.signInSilent();
        // this.userLoaded(user);
      } catch (error) {
        return;
      }
    }

    return user;
  };

  signInRedirect = (path?: string) => {
    path = path || window.location.pathname.substr(this.config.basePath.length);

    localStorage.setItem('redirectUri', window.location.pathname);
    this.userManager.signinRedirect({ state: path });
  };

  isAuthenticated = (): boolean => {
    const oidcStorage = JSON.parse(
      sessionStorage.getItem(`oidc.user:${this.config.authority}:${this.config.clientId}`) || '{}'
    );

    var authenticated = !!oidcStorage && !!oidcStorage.access_token;

    // Log.debug("AuthService:isAuthenticated", authenticated);

    return authenticated;
  };

  signInSilent = () => {
    // Log.debug("AuthService:signInSilent");
    return this.userManager.signinSilent();
  };

  signOut = () => {
    // Log.debug("AuthService:signOut");
    this.userManager.signoutRedirect({
      id_token_hint: localStorage.getItem('id_token')
    });
    this.userManager.clearStaleState();
  };

  signOutRedirectCallback = () => {
    // Log.debug("AuthService:signOutRedirectCallback");
    this.userManager.signoutRedirectCallback().then(() => {
      localStorage.clear();
    });
    this.userManager.clearStaleState();
  };

  private getPermissionsFromAccessToken = (token: string): string[] => {
    // var payload: any = jwt.decode(token);
    var payload: any = this.parseJwt(token);

    return (payload && payload.permissions) || [];
  };

  private parseJwt = (token: string) => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace('-', '+').replace('_', '/');
    return JSON.parse(window.atob(base64));
  };
}
